@layer components {
  .page {
    container-type: inline-size;
    display: grid;
    gap: var(--space-6);
  }

  .top {
    display: grid;
    gap: var(--space-4);
  }

  .intro {
    display: grid;
    gap: var(--space-4);
    align-items: end;
    padding-inline: var(--space-2);
    color: var(--color-on-frame);

    @container (inline-size >= 40rem) {
      grid-template-columns: minmax(0, 1fr) auto;
    }
  }

  .heading {
    display: grid;
    gap: var(--space-3);
  }

  .title {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1;
    text-shadow: var(--text-shadow-frame);
  }

  .usage {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);

    & div {
      display: flex;
      align-items: baseline;
      gap: 0.4rem;
      padding: 0.3rem 0.8rem;
      border-radius: var(--radius-full);
      background: var(--color-frame-pill);
    }

    & dt {
      color: var(--color-on-frame-muted);
      font-size: var(--text-xs);
      font-weight: 560;
    }

    & dd {
      margin: 0;
      font-size: var(--text-sm);
      font-weight: 700;
      font-variant-numeric: tabular-nums;
    }
  }

  .usageLink {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-self: start;
    gap: var(--space-2);
    /* The pills keep their size; the hit area around them reaches a thumb. */
    min-block-size: var(--tap-target);
    margin-block: calc((1.875rem - var(--tap-target)) / 2);
    color: inherit;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    @media (hover: hover) {
      &:hover .usage div,
      &:hover .usageMore {
        background: var(--color-frame-pill-hover);
      }
    }

    &:focus-visible {
      border-radius: var(--radius-full);
    }
  }

  .usageMore {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    min-block-size: 1.875rem;
    padding-inline: var(--space-3) var(--space-2);
    border-radius: var(--radius-full);
    background: var(--color-frame-pill);
    color: var(--color-on-frame);
    font-size: var(--text-xs);
    font-weight: 650;
    transition: background-color var(--duration-fast) var(--ease-out);

    & svg {
      inline-size: 0.875rem;
      block-size: 0.875rem;
      stroke-width: 2.6;
    }
  }

  .usage div {
    transition: background-color var(--duration-fast) var(--ease-out);
  }

  .usageSkeleton {
    inline-size: 20rem;
    max-inline-size: 100%;
    block-size: 1.875rem;
    background-color: var(--color-frame-pill);
  }

  .introActions {
    display: grid;
    gap: var(--space-2);

    @container (inline-size >= 40rem) {
      display: flex;
      align-items: center;
      gap: var(--space-4);
    }
  }

  .dropHint {
    display: none;
    color: var(--color-on-frame-muted);
    font-size: var(--text-sm);
    font-weight: 520;

    @media (hover: hover) and (pointer: fine) {
      @container (inline-size >= 40rem) {
        display: block;
      }
    }
  }

  .uploadButton {
    min-block-size: 3.25rem;
    padding-inline: var(--space-6);
    font-size: var(--text-base);
    font-weight: 700;
  }

  .filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-2) 0;
    padding-inline: var(--space-2);

    @container (inline-size >= 40rem) {
      grid-template-columns: minmax(0, 1fr) auto auto;
    }

    @container (inline-size >= 56rem) {
      grid-template-columns: minmax(0, 1fr) auto;
    }
  }

  .search {
    position: relative;
    display: flex;
    grid-column: 1 / -1;
    align-items: center;

    @container (inline-size >= 40rem) {
      grid-column: 1;
    }
  }

  .searchIcon {
    position: absolute;
    inset-inline-start: var(--space-4);
    inline-size: 1.125rem;
    block-size: 1.125rem;
    color: var(--color-text-muted);
    pointer-events: none;
  }

  .searchInput {
    padding-inline: calc(var(--space-4) + 1.125rem + var(--space-2)) var(--tap-target);
    font-size: max(16px, var(--text-sm));

    &::-webkit-search-cancel-button,
    &::-webkit-search-decoration {
      appearance: none;
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-focus);
      outline-offset: 2px;
    }
  }

  .clear {
    --color-focus: var(--color-accent);

    position: absolute;
    inset-inline-end: 0;
    display: grid;
    place-items: center;
    inline-size: var(--tap-target);
    block-size: var(--tap-target);
    border: 0;
    border-radius: var(--radius-full);
    background: none;
    color: var(--color-text-muted);
    transition:
      color var(--duration-fast) var(--ease-out),
      scale var(--duration-spring) var(--ease-spring);

    & svg {
      inline-size: 1.125rem;
      block-size: 1.125rem;
      stroke-width: 2.2;
    }

    &:focus-visible {
      outline-offset: -5px;
    }

    @media (hover: hover) {
      &:hover {
        color: var(--color-text);
      }
    }

    &:active {
      scale: 0.9;
    }
  }

  .sortField {
    display: flex;
    grid-column: 1;
    align-items: center;
    gap: var(--space-2);
    min-inline-size: 0;

    @container (inline-size >= 40rem) {
      grid-column: 2;
      margin-inline-start: var(--space-3);
    }

    @container (inline-size >= 56rem) {
      display: none;
    }
  }

  .sortLabel {
    color: var(--color-on-frame-muted);
    font-size: var(--text-sm);
    font-weight: 560;
  }

  .sortSelect {
    flex: 1;
    min-inline-size: 0;
    min-block-size: var(--tap-target);
    padding-inline: var(--space-3);
    border: 1.5px solid var(--color-border-strong);
    border-radius: var(--radius-md);
    background: var(--color-surface);
    color: var(--color-text);
    font-family: inherit;
    font-size: max(16px, var(--text-sm));
    font-weight: 620;
    box-shadow: inset 0 2px 0 var(--color-surface-sunken);
  }

  .count {
    grid-column: 2;
    justify-self: end;
    color: var(--color-on-frame-muted);

    @container (inline-size >= 40rem) {
      grid-column: 3;
    }

    @container (inline-size >= 56rem) {
      grid-column: 2;
    }

    font-size: var(--text-sm);
    font-weight: 560;
    font-variant-numeric: tabular-nums;

    &:not(:empty) {
      margin-inline-start: var(--space-3);
    }
  }

  .noMatch {
    --color-focus: var(--color-accent);

    display: grid;
    justify-items: center;
    align-content: center;
    gap: var(--space-3);
    padding: var(--space-10) var(--space-6);
    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
    text-align: center;
  }

  .noMatchTitle {
    max-inline-size: 32ch;
    margin-block-start: var(--space-2);
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.15;
    overflow-wrap: anywhere;
  }

  .noMatchDetail {
    max-inline-size: 40ch;
    margin-block-end: var(--space-2);
    color: var(--color-text-muted);
    text-wrap: balance;
  }

  .more {
    display: grid;
    justify-items: center;
  }

  .empty {
    --color-focus: var(--color-accent);

    display: grid;
    justify-items: center;
    align-content: center;
    gap: var(--space-3);
    min-block-size: min(34rem, 72dvh);
    padding: var(--space-10) var(--space-6);
    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
    text-align: center;
  }

  .emptyArt {
    display: flex;
    align-items: center;
    margin-block-end: var(--space-4);

    & > :first-child {
      margin-inline-end: -0.9rem;
      translate: 0 0.9rem;
      rotate: -14deg;
    }

    & > :last-child {
      margin-inline-start: -0.9rem;
      translate: 0 0.9rem;
      rotate: 12deg;
    }

    & > :nth-child(2) {
      position: relative;
      z-index: 1;
      animation: bob 3.2s ease-in-out infinite;
    }
  }

  @keyframes bob {
    50% {
      translate: 0 -0.35rem;
    }
  }

  .emptyTitle {
    font-family: var(--font-display);
    font-size: var(--text-2xl);
    font-weight: 700;
    letter-spacing: -0.01em;
    line-height: 1.05;
  }

  .emptyDetail {
    max-inline-size: 40ch;
    margin-block-end: var(--space-3);
    color: var(--color-text-muted);
    text-wrap: balance;
  }

  .pointerOnly {
    display: none;

    @media (hover: hover) and (pointer: fine) {
      display: inline;
    }
  }

  .touchOnly {
    @media (hover: hover) and (pointer: fine) {
      display: none;
    }
  }

  .pendingList {
    display: grid;
    padding: var(--space-3);
    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
  }

  .pendingRow {
    display: grid;
    grid-template-columns: 2.625rem minmax(0, 1fr);
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-3) var(--space-4);

    & span:first-child {
      block-size: 2.625rem;
      border-radius: var(--radius-sticker);
    }

    & span:last-child {
      block-size: 1rem;
      inline-size: 60%;
    }
  }

  .sharingStack {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 22rem), 1fr));
    align-items: start;
    gap: var(--space-3);
  }

  .sharingStack:has(> [open]) > * {
    grid-column: 1 / -1;
  }

  .sharing {
    container-type: inline-size;
    overflow: hidden;
  }

  .sharingSummary {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto;
    align-items: center;
    gap: var(--space-2) var(--space-3);
    min-block-size: 3.75rem;
    padding: var(--space-3) var(--space-5);
    cursor: pointer;
    list-style: none;
    transition: background-color var(--duration-fast) var(--ease-out);
    -webkit-tap-highlight-color: transparent;

    &::-webkit-details-marker {
      display: none;
    }

    @media (hover: hover) {
      &:hover {
        background: var(--color-surface-hover);
      }
    }

    &:focus-visible {
      outline: 2.5px solid var(--color-accent);
      outline-offset: -3px;
    }
  }

  .sharingTitle {
    font-family: var(--font-display);
    font-size: var(--text-lg);
    font-weight: 700;
  }

  .sharingMark {
    display: grid;
    place-items: center;
    inline-size: 2.5rem;
    block-size: 2.5rem;
    border-radius: var(--radius-blob);
    background: var(--color-accent-soft);
    color: var(--color-accent);
    rotate: -6deg;

    & svg {
      inline-size: 1.25rem;
      block-size: 1.25rem;
      stroke-width: 2.2;
    }
  }

  .sharingChevron {
    flex: none;
    color: var(--color-text-muted);
    rotate: 90deg;
    transition: rotate var(--duration-spring) var(--ease-spring);

    [open] > summary > & {
      rotate: -90deg;
    }
  }

  .sharingSummaryText {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-1) var(--space-3);
    min-inline-size: 0;
  }

  .sharingAudience {
    color: var(--color-text-muted);
    font-size: var(--text-sm);
    font-weight: 560;
    font-variant-numeric: tabular-nums;
  }

  .folderEmpty {
    --color-focus: var(--color-accent);

    display: grid;
    justify-items: center;
    align-content: center;
    gap: var(--space-3);
    min-block-size: min(22rem, 46dvh);
    padding: var(--space-10) var(--space-6);
    border-radius: var(--radius-sheet);
    background: var(--color-surface);
    box-shadow: var(--shadow-sheet);
    text-align: center;
  }

  .folderEmptyTitle {
    margin-block-start: var(--space-2);
    font-family: var(--font-display);
    font-size: var(--text-xl);
    font-weight: 600;
    line-height: 1.15;
  }

  .emptyActions {
    display: grid;
    gap: var(--space-2);
    justify-items: stretch;
    inline-size: min(20rem, 100%);
    margin-block-start: var(--space-2);

    @container (inline-size >= 30rem) {
      display: flex;
      inline-size: auto;
    }
  }
}
